import {
  autocompleteClasses,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  type SxProps,
} from "@mui/material";
import { type QueryClient, useMutation, type UseMutationOptions, useQueryClient } from "@tanstack/react-query";
import { AutocompleteField, CheckboxField } from "components/UI/Form";
import { Field, Form, Formik } from "formik";
import { type FC } from "react";
import toast from "react-hot-toast";
import usePermissions from "src/components/CustomHooks/usePermissions";
import { type WorkPacketOwner } from "src/types/work-packets";
import { bulkUpdateCases, type BulkUpdateCasesParams, type BulkUpdateCasesParamsInner } from "../../Cases/api/casesApi";
import { CaseStatus } from "../../Cases/api/constants";
import { useUserOptions } from "../useUserOptions";
import { CaseType } from "./case-types";
import { CASE_SUPERVISOR_PERMISSIONS } from "./CasePermission";
import { type ServerCaseFilters } from "./casesApi";

export interface CaseBulkActionPopupProps {
  filters: ServerCaseFilters;
  selectedItems: string[];
  excludedItems: string[];
  exclusionBased: boolean;
  caseType: CaseType;
  onClose: () => void;
  open: boolean;
  clearRowSelection: () => void;
}

type BulkUpdateParams = BulkUpdateCasesParams & { queryClient: QueryClient };

const bulkUpdateCasesOptions: UseMutationOptions<void, Error, BulkUpdateParams> = {
  mutationFn: bulkUpdateCases,
  mutationKey: ["bulk-update-cases"],

  onSuccess: async (_data, { queryClient }) => {
    queryClient.invalidateQueries({ queryKey: ["cases"] });
  },
};
interface FormState {
  CLOSE_WITHOUT_RESOLUTION: boolean;
  CASE_OWNER_ID: string;
  BACKUP_CASE_OWNER_ID: string;
  PROCESSOR_ID: string;
  BACKUP_PROCESSOR_ID: string;
}

const bulkUpdateGridSx: SxProps = {
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: 2,
  rowGap: 3,
  alignItems: "flex-start",
  [`& > .${autocompleteClasses.root}`]: {
    margin: "unset",
  },
};

export const CaseBulkActionPopup: FC<CaseBulkActionPopupProps> = ({
  filters,
  selectedItems,
  excludedItems,
  caseType,
  exclusionBased,
  onClose,
  open,
  clearRowSelection,
}) => {
  const queryClient = useQueryClient();
  const initialFormState: FormState = {
    CLOSE_WITHOUT_RESOLUTION: false,
    CASE_OWNER_ID: "",
    BACKUP_CASE_OWNER_ID: "",
    PROCESSOR_ID: "",
    BACKUP_PROCESSOR_ID: "",
  };
  const { mutateAsync, isPending } = useMutation(bulkUpdateCasesOptions);
  const onSubmit = async (formValues: FormState) => {
    const updateParams: BulkUpdateCasesParamsInner = {
      CASE_STATUS: formValues.CLOSE_WITHOUT_RESOLUTION ? CaseStatus.ClosedWithoutResolution : undefined,
      CASE_OWNER_ID: formValues.CASE_OWNER_ID || undefined,
      BACKUP_CASE_OWNER_ID: formValues.BACKUP_CASE_OWNER_ID || undefined,
      PROCESSOR_ID: formValues.PROCESSOR_ID || undefined,
      BACKUP_PROCESSOR_ID: formValues.BACKUP_PROCESSOR_ID || undefined,
    };
    const data: BulkUpdateParams = {
      filters,
      selectedItems: exclusionBased ? [] : selectedItems,
      excludedItems: exclusionBased ? excludedItems || [] : [],
      exclusionBased,
      caseType,
      updateParams,
      queryClient,
    };
    await mutateAsync(data);
    onClose();
    clearRowSelection();
    toast.success("Cases updated successfully");
  };

  const { hasPermission } = usePermissions();
  const isSupervisor = hasPermission(CASE_SUPERVISOR_PERMISSIONS[caseType]);
  const currentOwnerOptions = useUserOptions(isSupervisor);

  return (
    <Dialog open={open} onClose={() => onClose()} maxWidth="md" fullWidth>
      <DialogTitle>Bulk Actions</DialogTitle>
      <Formik initialValues={initialFormState} onSubmit={onSubmit}>
        {({ dirty, isValid, setFieldValue }) => (
          <Form>
            <DialogContent>
              <Box display="flex" flexDirection="column" gap={2}>
                {caseType !== CaseType.SHORTAGE_SETTLEMENT && (
                  <Field
                    type="checkbox"
                    name="CLOSE_WITHOUT_RESOLUTION"
                    component={CheckboxField}
                    label="Close selected cases without resolution"
                  />
                )}
              </Box>
              {caseType === CaseType.SHORTAGE_ESCALATION && isSupervisor && (
                <Box display="grid" sx={bulkUpdateGridSx} mt={3} gap={2}>
                  <Field
                    name="CASE_OWNER_ID"
                    component={AutocompleteField}
                    label="Case Owner"
                    options={currentOwnerOptions}
                    getOptionLabel={(option: WorkPacketOwner) => option.title}
                    placeholder="Case Owner"
                    setFieldValue={setFieldValue}
                    disabled={isPending}
                  />

                  <Field
                    name="BACKUP_CASE_OWNER_ID"
                    component={AutocompleteField}
                    label="Backup Case Owner"
                    options={currentOwnerOptions}
                    getOptionLabel={(option: WorkPacketOwner) => option.title}
                    placeholder="Backup Case Owner"
                    setFieldValue={setFieldValue}
                    disabled={isPending}
                  />

                  <Field
                    name="PROCESSOR_ID"
                    component={AutocompleteField}
                    label="Processor"
                    options={currentOwnerOptions}
                    getOptionLabel={(option: WorkPacketOwner) => option.title}
                    placeholder="Processor"
                    setFieldValue={setFieldValue}
                    disabled={isPending}
                  />

                  <Field
                    name="BACKUP_PROCESSOR_ID"
                    component={AutocompleteField}
                    label="Backup Processor"
                    options={currentOwnerOptions}
                    getOptionLabel={(option: WorkPacketOwner) => option.title}
                    placeholder="Backup Processor"
                    setFieldValue={setFieldValue}
                    disabled={isPending}
                  />
                </Box>
              )}
            </DialogContent>
            <DialogActions style={{ background: "transparent" }}>
              <Button variant="outlined" onClick={() => onClose()} disabled={isPending}>
                Cancel {isValid}
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={!dirty || !isValid || isPending}
                sx={{ paddingX: "24px" }}
              >
                <span>Save Changes</span>
                {isPending && <CircularProgress size={14} style={{ marginLeft: "12px", color: "#101828" }} />}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};
